import type { Content, Image } from '@plone/types';
import type { AccordionData } from '@redturtle/qubicaamf-theme/components/Blocks/Accordion/schema';
import { serializeNodesToText } from '@plone/volto-slate/editor/render';

interface QubicaContentWithImage extends Content {
  alternative_preview_image?: Image | null;
}

interface ContentEvent extends Content {
  event_url?: string;
  start: string;
  location?: string;
}

function getImageUrl(item: QubicaContentWithImage) {
  const sources = [
    item?.preview_image,
    item?.image,
    item?.alternative_preview_image,
  ];

  for (const source of sources) {
    if (source?.scales?.preview?.download) {
      return source.scales.preview.download;
    }
  }
  return null;
}

export function jsonldProduct(item: Content) {
  return {
    '@context': 'http://schema.org/',
    '@type': 'Product',
    name: item?.title,
    image: getImageUrl(item),
    description: item?.description,
    brand: {
      '@type': 'Thing',
      name: 'QubicaAMF',
    },
  };
}
export function jsonldEvent(event: ContentEvent) {
  return {
    '@context': 'http://schema.org',
    '@type': 'Event',
    name: event?.title,
    description: event?.description,
    url: event?.event_url,
    startDate: event?.start,
    location: {
      '@type': 'Place',
      name: event?.location,
      address: event?.location,
    },
  };
}
export function jsonldNews(news: Content) {
  return {
    '@context': 'http://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://google.com/article',
    },
    headline: news?.title,
    image: {
      '@type': 'ImageObject',
      url: getImageUrl(news),
      height: 400,
      width: 400,
    },
    datePublished: news?.effective,
    dateModified: news?.modified,
    author: {
      '@type': 'Person',
      name: news?.creators,
    },
    publisher: {
      '@type': 'Organization',
      name: 'QubicaAMF',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.qubicaamfbowling.com/qubicaminilogo.png',
        width: 453,
        height: 453,
      },
    },
    description: news?.description,
  };
}

export function jsonldBreadcrumbs(item: Content) {
  const breadcrumbs = item['@components']?.breadcrumbs?.items || [];
  return {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs?.map((breadcrumb, index) => {
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': breadcrumb['@id'],
          name: breadcrumb.title,
        },
      };
    }),
  };
}

export function jsonldContent(item: Content) {
  switch (item['@type']) {
    case 'Event':
      return jsonldEvent(item as ContentEvent);
    case 'Product':
      return jsonldProduct(item);
    case 'News Item':
      return jsonldNews(item);
    default:
      return null;
  }
}

export function jsonldHomepageSearch(item: Content) {
  if (item['@type'] !== 'Plone Site') {
    return null;
  }
  return {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: ' https://www.qubicaamfbowling.com',
    name: 'QubicaAMF',
    potentialAction: {
      '@type': 'SearchAction',
      target:
        'https://www.qubicaamfbowling.com/search?SearchableText={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

export function jsonldHomepage(item: Content) {
  if (item['@type'] !== 'Plone Site') {
    return null;
  }
  return {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    url: 'https://www.qubicaamfbowling.com',
    name: 'QubicaAMF',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+39-051-4192611',
        contactType: [
          'customer service',
          'technical support',
          'billing support',
          'sales',
          'emergency',
          'assistance',
          'package tracking',
        ],
        availableLanguage: [
          'English',
          'Italian',
          'French',
          'Spanish',
          'German',
          'Russian',
        ],
      },
      {
        '@type': 'ContactPoint',
        telephone: '+1-804-5691000',
        contactType: [
          'customer service',
          'technical support',
          'billing support',
          'sales',
          'emergency',
          'assistance',
          'package tracking',
        ],
        availableLanguage: ['English', 'Spanish'],
      },
      {
        '@type': 'ContactPoint',
        telephone: '+1-866-460-7263',
        contactType: [
          'customer service',
          'technical support',
          'billing support',
          'sales',
          'emergency',
          'assistance',
          'package tracking',
        ],
        contactOption: 'TollFree',
        areaServed: 'US',
        availableLanguage: ['English', 'Spanish'],
      },
    ],
    sameAs: [
      'https://www.facebook.com/QubicaAMFWorldwide/',
      'https://x.com/qubicaAMF',
      'https://www.youtube.com/user/QubicaAMFWorldwide',
    ],
    logo: 'https://www.qubicaamfbowling.com/qubicaminilogo.png',
  };
}

export function jsonldFAQ(items: AccordionData['items']) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((accordionItem) => {
      const answer = serializeNodesToText(accordionItem.text);
      return {
        '@type': 'Question',
        name: accordionItem.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      };
    }),
  };
}
